import React, {
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
} from "react";
import { useNavigate } from "react-router-dom";

// Components
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

// Icons
import {
    FolderOpen,
    Upload,
    CheckCircle,
    AlertCircle,
    Plus,
    X,
    ImageIcon,
    Loader2,
    Briefcase,
    MapPin,
    ArrowLeft,
    Sparkles,
    CheckCircle2,
    Eye,
    Edit,
    Trash2,
    Phone,
    Mail,
    Globe,
    Pencil,
    Info,
    Lightbulb,
    RefreshCw,
} from "lucide-react";

// Hooks & Config
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "@/components/ui/dialog";

// Constants
const SERVICE_TYPES = [
    "Mortgage Broker",
    "Plumbing",
    "Photography",
    "Staging",
    "General Contractor",
    "Cabinetry",
    "Junk Removal",
    "Wholesaler",
    "Electrical",
    "Landscaping",
    "Cleaning",
    "HVAC",
    "Roofing",
    "Painting",
    "Flooring",
    "Home Inspection",
    "Pest Control",
    "Moving Services",
    "Interior Design",
    "Property Management",
] as const;

const SERVICE_SOURCE_OPTIONS = [
    {
        value: "saved",
        label: "Your Saved Services",
        description: "Select from your existing services",
        icon: FolderOpen,
    },
    {
        value: "manual",
        label: "Add New Service",
        description: "Create a new service listing",
        icon: Upload,
    },
] as const;

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_PHOTOS = 2; // Only 2 photos for services
const MAX_FEATURES = 10;
const MAX_AUTO_IQ_ATTEMPTS = 3;

// Types
interface FeatureItem {
    id?: number;
    feature_name: string;
    name?: string; // Backend might return 'name' instead of 'feature_name'
}

interface ServiceData {
    title: string;
    serviceType: string;
    description: string;
    phone: string;
    email: string;
    website: string;
    address: string;
    subject: string;
    features: FeatureItem[];
}

interface PhotoItem {
    id: string;
    url: string;
    file?: File;
    index: number;  // ✅ ADD: Track the index
    isExisting?: boolean;  // ✅ ADD: Track if it's an existing photo
}

interface SavedService {
    id: string;
    title: string;
    service_type: string;
    description: string;
    phone?: string;
    email?: string;
    website?: string;
    address?: string;
    image?: string;
    hero_image_url?: string;
    photos?: string[];  // ✅ ADD: Array of photo URLs
    photos_urls?: string[];  // ✅ ADD: Absolute URLs
    subject?: string;
    features?: FeatureItem[];
    created_at?: string;
    owner_id?: string | number;
}

interface ValidationItem {
    label: string;
    isValid: boolean;
}

const getPersistedPhotoUrl = (url?: string | null) => {
    if (typeof url !== "string") return "";
    const trimmed = url.trim();
    if (!trimmed || trimmed.startsWith("blob:")) return "";
    return trimmed;
};

const getSecondaryPhotoUrls = (photoUrls: string[], heroImage: string) =>
    photoUrls.filter((url, index) => {
        const trimmedUrl = typeof url === "string" ? url.trim() : "";
        if (!trimmedUrl) return false;
        if (index === 0 && trimmedUrl === heroImage) return false;
        return trimmedUrl !== heroImage;
    });

const normalizePhotoPath = (url: string | null | undefined): string => {
    if (!url) return "";
    try {
        if (url.startsWith("http")) {
            return new URL(url).pathname.toLowerCase();
        }
    } catch {
        /* ignore */
    }
    return url.toLowerCase();
};

// Initial State
const initialServiceData: ServiceData = {
    title: "",
    serviceType: "",
    description: "",
    phone: "",
    email: "",
    website: "",
    address: "",
    subject: "",
    features: [],
};

// ============================================
// SERVICE STEP COMPONENT
// ============================================

interface ServiceStepProps {
    currentStep: number;
    handleBack: () => void;
    handleServiceSelect: (service: SavedService) => void;
    services: SavedService[];
    loadingServices: boolean;
    setCampaignSubject: (subject: string) => void;
    onEditService?: (service: SavedService) => void;
    onAutoAISelect?: (service: SavedService, templateId: number) => void;
    onServiceUpdate?: (updatedService: SavedService) => void;
    onServiceAdd?: (newService: SavedService) => void;
    onServiceDelete?: (serviceId: string | number) => void;
    onRefreshServices?: () => void;
    onEnhancedImageUpdate?: (payload: {
        id: string | number;
        originalUrl: string;
        enhancedUrl: string;
        isHero: boolean;
    }) => void;
    currentUserId?: string | number;
}

const ServiceStep: React.FC<ServiceStepProps> = ({
    currentStep,
    handleBack,
    handleServiceSelect,
    services,
    loadingServices,
    setCampaignSubject,
    onEditService,
    onAutoAISelect,
    onServiceUpdate,
    onServiceAdd,
    onServiceDelete,
    onRefreshServices,
    onEnhancedImageUpdate,
    currentUserId,
}) => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dropZoneRef = useRef<HTMLDivElement>(null);

    // State for service source selection
    const [serviceSource, setServiceSource] = useState<"saved" | "manual">("saved");

    // State for service form
    const [serviceData, setServiceData] = useState<ServiceData>(initialServiceData);
    const [photos, setPhotos] = useState<PhotoItem[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [features, setFeatures] = useState<FeatureItem[]>([]);

    // Custom service type
    const [showCustomServiceType, setShowCustomServiceType] = useState(false);
    const [customServiceType, setCustomServiceType] = useState("");

    // AI States
    const [isAutoIQLoading, setIsAutoIQLoading] = useState(false);
    const [autoIQCount, setAutoIQCount] = useState(0);
    const [autoAILoadingServiceId, setAutoAILoadingServiceId] = useState<string | null>(null);
    const [isAutoAIProcessing, setIsAutoAIProcessing] = useState(false);

    // Enhancement Dialog States
    const [enhanceDialogOpen, setEnhanceDialogOpen] = useState(false);
    const [enhanceDialogPhoto, setEnhanceDialogPhoto] = useState<PhotoItem | null>(null);
    const [enhancedPreviewUrl, setEnhancedPreviewUrl] = useState<string | null>(null);
    const [isEnhancingPreview, setIsEnhancingPreview] = useState(false);
    const [enhancedPhotoIds, setEnhancedPhotoIds] = useState<Set<string>>(new Set());
    const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);

    // Edit Dialog States
    const [editService, setEditService] = useState<SavedService | null>(null);
    const [editForm, setEditForm] = useState<SavedService | null>(null);
    const [editPhotos, setEditPhotos] = useState<PhotoItem[]>([]);
    const [editFeatures, setEditFeatures] = useState<FeatureItem[]>([]); // NEW: Features for edit
    const editFileInputRef = useRef<HTMLInputElement>(null);
    const [isEditDragging, setIsEditDragging] = useState(false);
    const editDropZoneRef = useRef<HTMLDivElement>(null);

    // Delete Dialog States
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [serviceToDelete, setServiceToDelete] = useState<string | number | null>(null);
    const [deleting, setDeleting] = useState(false);

    // Service List
    const [serviceList, setServiceList] = useState<SavedService[]>(services);

    // Touched fields for inline validation
    const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

    const markTouched = useCallback((field: string) => {
        setTouchedFields((prev) => new Set(prev).add(field));
    }, []);

    const markAllRequiredTouched = useCallback(() => {
        setTouchedFields(new Set(["title", "serviceType", "subject", "description", "photos", "features"]));
    }, []);

    const fieldError = useCallback(
        (field: string, value: string | undefined) =>
            touchedFields.has(field) && !value?.trim(),
        [touchedFields]
    );

    // Auto AI Template IDs for services
    const AUTO_AI_SERVICE_TEMPLATE_IDS = [1, 2, 3, 4];

    // Update service list when props change
    useEffect(() => {
        setServiceList((prev) => {
            if (prev.length === 0) return services;

            // Build a map of locally updated services by id
            const localMap = new Map(prev.map((s) => [String(s.id), s]));
            const parentMap = new Map(services.map((s) => [String(s.id), s]));

            // Use local version if it exists (it has latest edits),
            // add any new services from parent that don't exist locally
            const merged: SavedService[] = [];

            // Keep local order, use local data for existing items
            prev.forEach((s) => {
                if (parentMap.has(String(s.id))) {
                    merged.push(s); // keep local (updated) version
                }
                // if not in parent anymore, it was deleted — skip it
            });

            // Add any new services from parent not in local list
            services.forEach((s) => {
                if (!localMap.has(String(s.id))) {
                    merged.push(s);
                }
            });

            return merged;
        });
    }, [services]);

    // Helper function for full URL
    const getFullUrl = useCallback((url: string | null | undefined): string => {
        if (!url) return "";
        if (url.startsWith("http") || url.startsWith("blob:") || url.startsWith("data:")) {
            return url;
        }
        const baseUrl = axiosInstance.defaults.baseURL || "";
        return `${baseUrl}${url.startsWith("/") ? "" : "/"}${url}`;
    }, []);

    // Generate unique ID
    const generateId = () => `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    /** Turn an existing stored URL into a File so the API accepts it as hero_image */
    const existingPhotoUrlToFile = useCallback(
        async (photoUrl: string, filename: string): Promise<File | null> => {
            const fullUrl = getFullUrl(photoUrl);
            if (!fullUrl) return null;

            try {
                const response = await fetch(fullUrl);
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const blob = await response.blob();
                const ext = blob.type?.split("/")[1] || "jpg";
                return new File([blob], `${filename}.${ext}`, {
                    type: blob.type || "image/jpeg",
                });
            } catch (fetchErr) {
                try {
                    const proxyForm = new FormData();
                    proxyForm.append("image_url", fullUrl);
                    const uploadRes = await axiosInstance.post(endpoints.mls_uploads, proxyForm);
                    const proxiedUrl = uploadRes.data?.url || uploadRes.data?.path;
                    if (!proxiedUrl) return null;
                    const proxyFetch = await fetch(
                        proxiedUrl.startsWith("http") ? proxiedUrl : getFullUrl(proxiedUrl)
                    );
                    const blob = await proxyFetch.blob();
                    const ext = blob.type?.split("/")[1] || "jpg";
                    return new File([blob], `${filename}.${ext}`, {
                        type: blob.type || "image/jpeg",
                    });
                } catch (proxyErr) {
                    console.error("Could not convert existing photo to file:", fetchErr, proxyErr);
                    return null;
                }
            }
        },
        [getFullUrl]
    );

    // ==========================================
    // HELPER: Normalize feature data
    // ==========================================
    const normalizeFeatures = useCallback((featuresData: any[]): FeatureItem[] => {
        if (!Array.isArray(featuresData)) return [];

        return featuresData.map((f, idx) => ({
            id: f.id || undefined,
            feature_name: f.feature_name || f.name || "",
            name: f.name || f.feature_name || "",
        })).filter(f => f.feature_name.trim() !== "");
    }, []);

    // ==========================================
    // FEATURE HANDLERS (for new service form)
    // ==========================================
    const addFeature = useCallback(() => {
        if (features.length >= MAX_FEATURES) {
            toast({
                title: "Feature Limit Reached",
                description: `You can add a maximum of ${MAX_FEATURES} features.`,
                variant: "destructive",
                duration: 1000
            });
            return;
        }
        setFeatures((prev) => [...prev, { feature_name: "" }]);
    }, [features.length, toast]);

    const handleFeatureChange = useCallback((index: number, value: string) => {
        setFeatures((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], feature_name: value };
            return updated;
        });
    }, []);

    const handleFeatureBlur = useCallback((index: number) => {
        setFeatures((prev) => {
            const feature = prev[index];
            if (!feature?.feature_name?.trim() && !feature?.id) {
                return prev.filter((_, i) => i !== index);
            }
            return prev;
        });
    }, []);

    const handleFeatureDelete = useCallback((index: number) => {
        setFeatures((prev) => {
            const featureToDelete = prev[index];
            const updated = prev.filter((_, i) => i !== index);
            if (featureToDelete?.feature_name?.trim() || featureToDelete?.id) {
                toast({
                    title: "Feature Removed",
                    description: "Feature has been deleted.",
                    duration: 1000
                });
            }
            return updated;
        });
    }, [toast]);

    // ==========================================
    // EDIT FEATURE HANDLERS (for edit dialog)
    // ==========================================
    const addEditFeature = useCallback(() => {
        if (editFeatures.length >= MAX_FEATURES) {
            toast({
                title: "Feature Limit Reached",
                description: `You can add a maximum of ${MAX_FEATURES} features.`,
                variant: "destructive",
                duration: 1000
            });
            return;
        }
        setEditFeatures((prev) => [...prev, { feature_name: "" }]);
    }, [editFeatures.length, toast]);

    const handleEditFeatureChange = useCallback((index: number, value: string) => {
        setEditFeatures((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], feature_name: value, name: value };
            return updated;
        });
    }, []);

    const handleEditFeatureBlur = useCallback((index: number) => {
        setEditFeatures((prev) => {
            const feature = prev[index];
            if (!feature?.feature_name?.trim() && !feature?.id) {
                return prev.filter((_, i) => i !== index);
            }
            return prev;
        });
    }, []);

    const handleEditFeatureDelete = useCallback((index: number) => {
        setEditFeatures((prev) => {
            const featureToDelete = prev[index];
            const updated = prev.filter((_, i) => i !== index);
            if (featureToDelete?.feature_name?.trim() || featureToDelete?.id) {
                toast({
                    title: "Feature Removed",
                    description: "Feature has been deleted.",
                    duration: 1000
                });
            }
            return updated;
        });
    }, [toast]);

    // ==========================================
    // SERVICE TYPE HANDLERS
    // ==========================================
    const getServiceTypeSelectValue = useCallback(() => {
        if (showCustomServiceType) return "custom";
        return serviceData.serviceType || "";
    }, [showCustomServiceType, serviceData.serviceType]);

    const handleServiceTypeChange = useCallback((value: string) => {
        if (value === "custom") {
            setShowCustomServiceType(true);
            setCustomServiceType("");
            setServiceData((prev) => ({ ...prev, serviceType: "" }));
        } else {
            setShowCustomServiceType(false);
            setCustomServiceType("");
            setServiceData((prev) => ({ ...prev, serviceType: value }));
        }
    }, []);

    const handleCustomServiceTypeInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCustomServiceType(value);
        setServiceData((prev) => ({ ...prev, serviceType: value }));
    }, []);

    const isValidServiceType = useCallback(() => {
        if (showCustomServiceType) {
            return customServiceType.trim().length > 0;
        }
        return serviceData.serviceType.length > 0;
    }, [showCustomServiceType, customServiceType, serviceData.serviceType]);

    // ==========================================
    // INPUT HANDLERS
    // ==========================================
    const handleInputChange = useCallback((field: keyof ServiceData, value: string) => {
        setServiceData((prev) => ({ ...prev, [field]: value }));
    }, []);

    // ==========================================
    // AUTO IQ HANDLER
    // ==========================================
    const handleAutoIQ = useCallback(async () => {
        if (isAutoIQLoading || autoIQCount >= MAX_AUTO_IQ_ATTEMPTS) return;

        const desc = serviceData.description.trim();

        if (desc.length < 10) {
            toast({
                title: "Description too short",
                description: "Please type at least 10 characters before using Auto IQ.",
                variant: "destructive",
                duration: 1000
            });
            return;
        }

        setIsAutoIQLoading(true);

        try {
            // Generate improved description
            const descPayload = {
                mode: "service",
                description: desc,
                service_type: serviceData.serviceType,
            };

            const descResp = await axiosInstance.post(endpoints.generate_description, descPayload);
            const improvedDesc = descResp.data?.description?.trim();

            if (!improvedDesc) {
                throw new Error("No description returned from AI");
            }

            setServiceData((prev) => ({
                ...prev,
                description: improvedDesc,
            }));

            // Generate subject line
            const subjectPayload = {
                description: improvedDesc,
                service_type: serviceData.serviceType,
            };

            const subjectResp = await axiosInstance.post(endpoints.generate_subject, subjectPayload);
            const subjects = subjectResp.data?.subjects || [];

            if (subjects.length > 0) {
                setServiceData((prev) => ({
                    ...prev,
                    subject: subjects[0],
                }));
            }

            toast({
                title: "✨ Auto IQ Applied!",
                description: "Your description was improved and a subject line was generated.",
                duration: 1000
            });

            setAutoIQCount((prev) => prev + 1);
        } catch (err: any) {
            console.error("Auto IQ error:", err);
            toast({
                title: "Auto IQ Failed",
                description: err.response?.data?.error || "Could not generate content. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsAutoIQLoading(false);
        }
    }, [isAutoIQLoading, autoIQCount, serviceData.description, serviceData.serviceType, toast]);

    // ==========================================
    // AUTO AI FOR SAVED SERVICES
    // ==========================================
    const handleAutoAIClick = useCallback((service: SavedService) => {
        if (autoAILoadingServiceId) return;

        setAutoAILoadingServiceId(service.id);

        setTimeout(() => {
            const randomIndex = Math.floor(Math.random() * AUTO_AI_SERVICE_TEMPLATE_IDS.length);
            const selectedTemplateId = AUTO_AI_SERVICE_TEMPLATE_IDS[randomIndex];

            if (service.subject) {
                setCampaignSubject(service.subject);
            }

            if (onAutoAISelect) {
                onAutoAISelect(service, selectedTemplateId);
            }

            setAutoAILoadingServiceId(null);
        }, 800);
    }, [autoAILoadingServiceId, setCampaignSubject, onAutoAISelect]);

    // ==========================================
    // VALIDATION
    // ==========================================
    const getValidationItems = useCallback((): ValidationItem[] => {
        return [
            { label: "Title", isValid: !!serviceData.title.trim() },
            { label: "Service Type", isValid: isValidServiceType() },
            { label: "Description", isValid: !!serviceData.description.trim() },
            { label: "Email Subject", isValid: !!serviceData.subject.trim() },
            { label: `Images (${photos.length}/${MAX_PHOTOS})`, isValid: photos.length >= 1 },
            { label: `Features (${features.filter(f => f.feature_name.trim()).length})`, isValid: features.filter(f => f.feature_name.trim()).length >= 1 },
        ];
    }, [serviceData, photos.length, features, isValidServiceType]);

    // ==========================================
    // FILE HANDLING
    // ==========================================
    const generatePhotoId = (index: number) =>
        `photo_${index}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Updated processFiles function
    const processFiles = useCallback((files: FileList | File[]) => {
        const fileArray = Array.from(files);
        const remainingSlots = MAX_PHOTOS - photos.length;

        if (remainingSlots <= 0) {
            toast({
                title: "Maximum Photos Reached",
                description: `You can only upload up to ${MAX_PHOTOS} photos.`,
                variant: "destructive",
                duration: 1000
            });
            return;
        }

        const filesToProcess = fileArray.slice(0, remainingSlots);
        const validPhotos: PhotoItem[] = [];

        filesToProcess.forEach((file, idx) => {
            const allowedExt = /\.(jpe?g|png)$/i;
            if (!allowedExt.test(file.name || "")) {
                toast({
                    title: "Not Supported",
                    description: `${file.name} is not supported. Only JPG, JPEG, PNG allowed.`,
                    variant: "destructive",
                    duration: 1000
                });
                return;
            }

            if (!file.type.startsWith("image/")) {
                toast({
                    title: "Invalid File Type",
                    description: `${file.name} is not an image file.`,
                    variant: "destructive",
                    duration: 1000
                });
                return;
            }

            if (file.size > MAX_IMAGE_SIZE) {
                const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
                toast({
                    title: "File Too Large",
                    description: `${file.name} (${fileSizeMB} MB) exceeds 5 MB limit.`,
                    variant: "destructive",
                    duration: 1000
                });
                return;
            }

            const currentIndex = photos.length + validPhotos.length;
            const url = URL.createObjectURL(file);

            validPhotos.push({
                id: generatePhotoId(currentIndex),
                url,
                file,
                index: currentIndex,
                isExisting: false,
            });
        });

        if (validPhotos.length > 0) {
            setPhotos((prev) => {
                const updated = [...prev, ...validPhotos];
                // Re-index all photos
                return updated.map((photo, idx) => ({
                    ...photo,
                    index: idx,
                }));
            });

            toast({
                title: "Photos Added",
                description: `${validPhotos.length} photo${validPhotos.length > 1 ? "s" : ""} uploaded successfully!`,
                duration: 1000
            });
        }
    }, [photos.length, toast]);

    const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            processFiles(files);
        }
        event.target.value = "";
    }, [processFiles]);

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
            setIsDragging(false);
        }
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            processFiles(files);
        }
    }, [processFiles]);

    const removePhoto = useCallback((photoId: string) => {
        setPhotos((prev) => {
            const photoToRemove = prev.find((p) => p.id === photoId);
            if (photoToRemove && photoToRemove.file) {
                URL.revokeObjectURL(photoToRemove.url);
            }

            // Remove and re-index
            const remaining = prev.filter((p) => p.id !== photoId);
            return remaining.map((photo, idx) => ({
                ...photo,
                index: idx,
            }));
        });

        toast({
            title: "Photo Removed",
            description: "Photo has been removed.",
            duration: 1000
        });
    }, [toast]);

    const setAsMainPhoto = useCallback((photoId: string) => {
        setPhotos((prev) => {
            const photoIndex = prev.findIndex((p) => p.id === photoId);
            if (photoIndex <= 0) return prev;

            const newPhotos = [...prev];
            const [photo] = newPhotos.splice(photoIndex, 1);
            newPhotos.unshift(photo);

            // Re-index all photos
            return newPhotos.map((p, idx) => ({
                ...p,
                index: idx,
            }));
        });

        toast({
            title: "Main Photo Updated",
            description: "Photo has been set as the main image.",
            duration: 1000
        });
    }, [toast]);

    // ==========================================
    // IMAGE ENHANCEMENT
    // ==========================================
    const handleEnhanceImage = useCallback(async (photo: PhotoItem) => {
        if (!photo.file || enhancedPhotoIds.has(photo.id)) return;

        setOriginalImageUrl(photo.url);
        setEnhanceDialogPhoto({ ...photo });
        setEnhancedPreviewUrl(null);
        setIsEnhancingPreview(true);
        setEnhanceDialogOpen(true);

        try {
            const formData = new FormData();
            formData.append("image", photo.file);

            const response = await axiosInstance.post(endpoints.enhance_image, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (response.data?.success && response.data?.enhanced_image) {
                setEnhancedPreviewUrl(response.data.enhanced_image);
                setEnhancedPhotoIds((prev) => new Set(prev).add(photo.id));
            } else {
                throw new Error("Enhancement failed - no URL returned");
            }
        } catch (error: any) {
            console.error("Enhancement error:", error);
            toast({
                title: "Enhancement Failed",
                description: error.response?.data?.error || "Please try again later.",
                variant: "destructive",
                duration: 1000
            });
            setEnhanceDialogOpen(false);
            setEnhanceDialogPhoto(null);
            setOriginalImageUrl(null);
            setEnhancedPreviewUrl(null);
        } finally {
            setIsEnhancingPreview(false);
        }
    }, [enhancedPhotoIds, toast]);

    const handleApplyEnhancement = useCallback(() => {
        if (!enhanceDialogPhoto || !enhancedPreviewUrl) return;

        setPhotos((prev) =>
            prev.map((p) =>
                p.id === enhanceDialogPhoto.id ? { ...p, url: enhancedPreviewUrl } : p
            )
        );

        const isMainPhoto = photos[0]?.id === enhanceDialogPhoto.id;
        if (onEnhancedImageUpdate) {
            onEnhancedImageUpdate({
                id: enhanceDialogPhoto.id,
                originalUrl: enhanceDialogPhoto.url,
                enhancedUrl: enhancedPreviewUrl,
                isHero: isMainPhoto,
            });
        }

        setEnhancedPhotoIds((prev) => new Set(prev).add(enhanceDialogPhoto.id));

        toast({
            title: "Image Enhanced ✨",
            description: "AI enhancement applied successfully!",
            duration: 1000
        });

        setEnhanceDialogOpen(false);
        setEnhanceDialogPhoto(null);
        setOriginalImageUrl(null);
        setEnhancedPreviewUrl(null);
    }, [enhanceDialogPhoto, enhancedPreviewUrl, onEnhancedImageUpdate, photos, toast]);

    // ==========================================
    // RESET FORM
    // ==========================================
    const handleResetForm = useCallback(() => {
        setServiceData(initialServiceData);
        setPhotos([]);
        setFeatures([]);
        setShowCustomServiceType(false);
        setCustomServiceType("");
        setAutoIQCount(0);
        setTouchedFields(new Set());
    }, []);

    const handleSourceChange = useCallback((value: string) => {
        setServiceSource(value as "saved" | "manual");
        handleResetForm();
    }, [handleResetForm]);

    // ==========================================
    // DELETE HANDLERS
    // ==========================================
    const openDeleteDialog = useCallback((serviceId: string) => {
        setServiceToDelete(serviceId);
        setDeleteDialogOpen(true);
    }, []);

    const closeDeleteDialog = useCallback(() => {
        setDeleteDialogOpen(false);
        setServiceToDelete(null);
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!serviceToDelete) return;

        setDeleting(true);
        try {
            await axiosInstance.delete(`${endpoints.service_listings}${serviceToDelete}/`);

            // ✅ UPDATE LOCAL STATE IMMEDIATELY
            setServiceList((prev) => prev.filter((s) => String(s.id) !== String(serviceToDelete)));

            // ✅ NOTIFY PARENT COMPONENT
            if (onServiceDelete) {
                onServiceDelete(serviceToDelete);
            }

            if (editService?.id === serviceToDelete) {
                setEditService(null);
                setEditForm(null);
                setEditPhotos([]);
                setEditFeatures([]);
            }

            toast({
                title: "Service Deleted",
                description: "The service has been removed permanently.",
                duration: 1000
            });
        } catch (err: any) {
            toast({
                title: "Deletion Failed",
                description: err.response?.data?.message || err.response?.data?.error || "Could not delete the service.",
                variant: "destructive",
            });
        } finally {
            setDeleting(false);
            closeDeleteDialog();
        }
    }, [
        serviceToDelete,
        editService,
        toast,
        closeDeleteDialog,
        onServiceDelete, // ✅ ADD TO DEPENDENCIES
    ]);

    useEffect(() => {
        if (!editService) {
            setEditPhotos([]);
            setEditForm(null);
            setEditFeatures([]);
            return;
        }

        setEditForm({ ...editService });

        const existingPhotos: PhotoItem[] = [];
        const mainImageUrl = editService.hero_image_url || editService.image;

        if (mainImageUrl) {
            const fullUrl = getFullUrl(mainImageUrl);
            // ✅ Only add if URL is valid and not empty
            if (fullUrl && fullUrl.trim() !== "") {
                existingPhotos.push({
                    id: generateId(),
                    url: fullUrl,
                    file: undefined,
                    index: 0,
                    isExisting: true,
                });
            }
        }

        const mainPath = normalizePhotoPath(
            mainImageUrl ? getFullUrl(mainImageUrl) : ""
        );

        const addSecondaryIfNew = (photoUrl: string) => {
            if (!photoUrl || typeof photoUrl !== "string") return;
            const full = getFullUrl(photoUrl.trim());
            if (!full || full.trim() === "") return;
            if (mainPath && normalizePhotoPath(full) === mainPath) return;
            // ✅ Avoid duplicates
            if (existingPhotos.some(p => normalizePhotoPath(p.url) === normalizePhotoPath(full))) return;

            existingPhotos.push({
                id: generateId(),
                url: full,
                file: undefined,
                index: existingPhotos.length,
                isExisting: true,
            });
        };

        // ✅ Use photos_urls first (absolute), fall back to photos (relative)
        const photoList = editService.photos_urls?.length
            ? editService.photos_urls
            : editService.photos || [];

        photoList.forEach((photoUrl: string) => addSecondaryIfNew(photoUrl));

        setEditPhotos(existingPhotos);

        // Load features
        if (editService.features && Array.isArray(editService.features)) {
            const normalizedFeatures = editService.features
                .map((f: any) => ({
                    id: f.id || undefined,
                    feature_name: f.feature_name || f.name || "",
                    name: f.name || f.feature_name || "",
                }))
                .filter((f: FeatureItem) => (f.feature_name || "").trim() !== "");
            setEditFeatures(normalizedFeatures);
        } else {
            setEditFeatures([]);
        }
    }, [editService, getFullUrl]);

    const processEditFiles = useCallback((files: FileList | File[]) => {
        const fileArray = Array.from(files);
        const remainingSlots = MAX_PHOTOS - editPhotos.length;

        if (remainingSlots <= 0) {
            toast({
                title: "Maximum Photos Reached",
                description: `You can only upload up to ${MAX_PHOTOS} photos.`,
                variant: "destructive",
                duration: 1000
            });
            return;
        }

        const filesToProcess = fileArray.slice(0, remainingSlots);
        const validPhotos: PhotoItem[] = [];

        filesToProcess.forEach((file, idx) => {
            const allowedExt = /\.(jpe?g|png)$/i;
            if (!allowedExt.test(file.name || "")) {
                toast({
                    title: "Not Supported",
                    description: `${file.name} is not supported. Only JPG, JPEG, PNG allowed.`,
                    variant: "destructive",
                    duration: 1000
                });
                return;
            }

            if (!file.type.startsWith("image/")) {
                toast({
                    title: "Invalid File Type",
                    description: `${file.name} is not an image file.`,
                    variant: "destructive",
                    duration: 1000
                });
                return;
            }

            if (file.size > MAX_IMAGE_SIZE) {
                const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
                toast({
                    title: "File Too Large",
                    description: `${file.name} (${fileSizeMB} MB) exceeds 5 MB limit.`,
                    variant: "destructive",
                    duration: 1000
                });
                return;
            }

            const currentIndex = editPhotos.length + validPhotos.length;
            const url = URL.createObjectURL(file);

            validPhotos.push({
                id: generateId(),
                url,
                file,
                index: currentIndex,  // ✅ ADD index
                isExisting: false,    // ✅ ADD isExisting flag
            });
        });

        if (validPhotos.length > 0) {
            setEditPhotos((prev) => {
                const updated = [...prev, ...validPhotos];
                // Re-index all photos
                return updated.map((photo, idx) => ({
                    ...photo,
                    index: idx,
                }));
            });

            toast({
                title: "Photos Added",
                description: `${validPhotos.length} photo${validPhotos.length > 1 ? "s" : ""} uploaded!`,
                duration: 1000
            });
        }
    }, [editPhotos.length, toast]);

    const handleEditFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            processEditFiles(files);
        }
        event.target.value = "";
    }, [processEditFiles]);

    const removeEditPhoto = useCallback((photoId: string) => {
        setEditPhotos((prev) => {
            const photoToRemove = prev.find((p) => p.id === photoId);
            if (photoToRemove && photoToRemove.file) {
                URL.revokeObjectURL(photoToRemove.url);
            }
            return prev.filter((p) => p.id !== photoId);
        });
        toast({
            title: "Photo Removed",
            description: "Photo has been removed.",
            duration: 1000
        });
    }, [toast]);

    const setEditAsMainPhoto = useCallback((photoId: string) => {
        setEditPhotos((prev) => {
            const photoIndex = prev.findIndex((p) => p.id === photoId);
            if (photoIndex <= 0) return prev;
            const newPhotos = [...prev];
            const [photo] = newPhotos.splice(photoIndex, 1);
            newPhotos.unshift(photo);
            return newPhotos;
        });
        toast({
            title: "Main Photo Updated",
            description: "Photo has been set as the main image.",
            duration: 1000
        });
    }, [toast]);
    const replaceEditPhotoAtIndex = useCallback((index: number, file: File) => {
        const url = URL.createObjectURL(file);

        setEditPhotos((prev) => {
            const updated = [...prev];

            // Revoke old URL if it's a blob URL
            if (updated[index]?.url?.startsWith('blob:')) {
                URL.revokeObjectURL(updated[index].url);
            }

            updated[index] = {
                id: generateId(),
                url,
                file,
                index,
                isExisting: false,
            };

            return updated;
        });

        toast({
            title: "Photo Replaced",
            description: `Photo ${index + 1} has been updated.`,
            duration: 1000
        });
    }, [toast]);

    const handleEditDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsEditDragging(true);
    }, []);

    const handleEditDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (editDropZoneRef.current && !editDropZoneRef.current.contains(e.relatedTarget as Node)) {
            setIsEditDragging(false);
        }
    }, []);

    const handleEditDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleEditDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsEditDragging(false);
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            processEditFiles(files);
        }
    }, [processEditFiles]);

    const handleAddServiceAndSelect = useCallback(async () => {
        markAllRequiredTouched();

        const validationItems = getValidationItems();
        const missingFields = validationItems
            .filter((item) => !item.isValid)
            .map((item) => item.label);
        if (missingFields.length > 0) {
            toast({
                title: "Missing Required Fields",
                description: `Please fill: ${missingFields.join(", ")}`,
                variant: "destructive",
                duration: 1000
            });
            return;
        }

        setIsSaving(true);

        try {
            // Normalize features
            const validFeatures = features
                .filter((f) => {
                    const name = (f.feature_name || f.name || "").toString().trim();
                    return name.length > 0;
                })
                .map((f) => {
                    const name = (f.feature_name || f.name || "").toString().trim();
                    return { name };
                });

            // Check if we have any new file uploads
            const hasNewFiles = photos.some((p) => p.file instanceof File);

            let createResponse;

            if (hasNewFiles) {
                // WITH FILES → FormData
                const formData = new FormData();

                // Required fields
                formData.append("title", serviceData.title.trim());
                formData.append("headline", serviceData.title.trim());
                formData.append("service_type", serviceData.serviceType || "");

                // Optional fields
                if (serviceData.description?.trim()) {
                    formData.append("description", serviceData.description.trim());
                }
                if (serviceData.subject?.trim()) {
                    formData.append("subject", serviceData.subject.trim());
                }
                if (serviceData.phone?.trim()) {
                    formData.append("phone", serviceData.phone.trim());
                }
                if (serviceData.email?.trim()) {
                    formData.append("email", serviceData.email.trim());
                }
                if (serviceData.website?.trim()) {
                    formData.append("website", serviceData.website.trim());
                }
                if (serviceData.address?.trim()) {
                    formData.append("address", serviceData.address.trim());
                }

                // Features
                formData.append("features", JSON.stringify(validFeatures));

                // Photos
                photos.forEach((photo, index) => {
                    if (photo.file instanceof File) {
                        if (index === 0) {
                            formData.append("hero_image", photo.file, photo.file.name);
                        } else if (index === 1) {
                            formData.append("photo_1", photo.file, photo.file.name);
                        }
                    }
                });

                createResponse = await axiosInstance.post(
                    endpoints.service_listings,
                    formData,
                    {
                        headers: { "Content-Type": undefined },
                        transformRequest: [(data) => data],
                    }
                );
            } else {
                // WITHOUT FILES → JSON
                const existingPhotoUrls = photos
                    .filter((p, index) => index > 0 && p.url && !p.url.startsWith("blob:"))
                    .map((p) => p.url);

                const payload = {
                    title: serviceData.title.trim(),
                    headline: serviceData.title.trim(),
                    description: serviceData.description?.trim() || "",
                    service_type: serviceData.serviceType || "",
                    subject: serviceData.subject?.trim() || "",
                    phone: serviceData.phone?.trim() || "",
                    email: serviceData.email?.trim() || "",
                    website: serviceData.website?.trim() || "",
                    address: serviceData.address?.trim() || "",
                    features: validFeatures,
                    photos: existingPhotoUrls,
                };

                createResponse = await axiosInstance.post(
                    endpoints.service_listings,
                    payload,
                    { headers: { "Content-Type": "application/json" } }
                );
            }

            const serverData = createResponse.data;
            const localPhotoUrls = photos
                .map((photo) => getPersistedPhotoUrl(photo.url))
                .filter((url) => url !== "");
            const preferredHeroImage =
                localPhotoUrls[0] || serverData.hero_image_url || "";
            const serverSecondaryPhotos = getSecondaryPhotoUrls(
                serverData.photos_urls || serverData.photos || [],
                preferredHeroImage
            );
            const preferredAdditionalPhotos =
                localPhotoUrls.length > 1
                    ? localPhotoUrls.slice(1)
                    : serverSecondaryPhotos;

            const newService: SavedService = {
                id: String(serverData.id),
                title: serverData.title,
                service_type: serverData.service_type,
                description: serverData.description || "",
                phone: serverData.phone || "",
                email: serverData.email || "",
                website: serverData.website || "",
                address: serverData.address || "",
                image: preferredHeroImage,
                hero_image_url: preferredHeroImage,
                photos: preferredAdditionalPhotos,
                photos_urls: preferredAdditionalPhotos,
                subject: serverData.subject || "",
                features: serverData.features || [],
            };

            // ✅ UPDATE LOCAL STATE IMMEDIATELY
            setServiceList((prev) => [newService, ...prev]);

            // ✅ NOTIFY PARENT COMPONENT
            if (onServiceAdd) {
                onServiceAdd(newService);
            }

            toast({
                title: "✅ Service Created!",
                description: `Service added with ${serverData.features?.length || 0} features!`,
                duration: 1000
            });

            if (serverData.subject) {
                setCampaignSubject(serverData.subject);
            }

            // Reset form
            handleResetForm();

            // Switch to saved services view
            setServiceSource("saved");

            handleServiceSelect(newService);
        } catch (error: any) {
            console.error("Create service error:", error);

            let errorMessage = "Failed to add service";
            if (error.response?.data) {
                const data = error.response.data;
                if (data.error) errorMessage = data.error;
                else if (data.detail) errorMessage = data.detail;
                else {
                    errorMessage = Object.entries(data)
                        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
                        .join("; ");
                }
            }
            toast({
                title: "Error",
                description: errorMessage,
                variant: "destructive",
            });
        } finally {
            setIsSaving(false);
        }
    }, [
        getValidationItems,
        serviceData,
        photos,
        features,
        toast,
        setCampaignSubject,
        handleServiceSelect,
        handleResetForm,
        onServiceAdd,
        markAllRequiredTouched,
    ]);

    const handleAutoAIForManualEntry = useCallback(async () => {
        markAllRequiredTouched();

        const validationItems = getValidationItems();
        const missingFields = validationItems
            .filter((item) => !item.isValid)
            .map((item) => item.label);

        if (missingFields.length > 0) {
            toast({
                title: "Missing Required Fields",
                description: `Please fill: ${missingFields.join(", ")}`,
                variant: "destructive",
                duration: 1000
            });
            return;
        }

        setIsAutoAIProcessing(true);

        try {
            const formData = new FormData();

            formData.append("title", serviceData.title.trim());
            formData.append("service_type", serviceData.serviceType);
            formData.append("description", serviceData.description);

            if (serviceData.subject?.trim()) {
                formData.append("subject", serviceData.subject.trim());
            }
            if (serviceData.phone?.trim()) {
                formData.append("phone", serviceData.phone.trim());
            }
            if (serviceData.email?.trim()) {
                formData.append("email", serviceData.email.trim());
            }
            if (serviceData.website?.trim()) {
                formData.append("website", serviceData.website.trim());
            }
            if (serviceData.address?.trim()) {
                formData.append("address", serviceData.address.trim());
            }
            if (serviceData.title?.trim()) {
                formData.append("headline", serviceData.title.trim());
            }

            // Features
            const validFeatures = features
                .filter((f) => (f.feature_name || f.name || "").trim().length > 0)
                .map((f) => ({
                    name: (f.feature_name || f.name || "").trim(),
                }));

            formData.append("features", JSON.stringify(validFeatures));

            if (photos.length > 0 && photos[0]?.file instanceof File) {
                formData.append("hero_image", photos[0]?.file);
            } else {
                formData.append("hero_image", photos[0]?.url || null);
            }

            const createResponse = await axiosInstance.post(endpoints.service_listings, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            const serverData = createResponse.data;
            const localPhotoUrls = photos
                .map((photo) => getPersistedPhotoUrl(photo.url))
                .filter((url) => url !== "");
            const preferredHeroImage =
                localPhotoUrls[0] ||
                serverData.hero_image_url ||
                getFullUrl(serverData.hero_image) ||
                "";
            const serverSecondaryPhotos = getSecondaryPhotoUrls(
                serverData.photos_urls || serverData.photos || [],
                preferredHeroImage
            );
            const preferredAdditionalPhotos =
                localPhotoUrls.length > 1
                    ? localPhotoUrls.slice(1)
                    : serverSecondaryPhotos;

            const newService: SavedService = {
                id: String(serverData.id),
                title: serverData.title || serviceData.title,
                service_type: serverData.service_type || serviceData.serviceType,
                description: serverData.description || serviceData.description,
                phone: serverData.phone || "",
                email: serverData.email || "",
                website: serverData.website || "",
                address: serverData.address || "",
                image: preferredHeroImage,
                hero_image_url: preferredHeroImage,
                photos: preferredAdditionalPhotos,
                photos_urls: preferredAdditionalPhotos,
                subject: serverData.subject || serviceData.subject?.trim() || "",
                features: serverData.features || [],
            };

            // ✅ UPDATE LOCAL STATE IMMEDIATELY
            setServiceList((prev) => [newService, ...prev]);

            // ✅ NOTIFY PARENT COMPONENT
            if (onServiceAdd) {
                onServiceAdd(newService);
            }

            if (serviceData.subject?.trim()) {
                setCampaignSubject(serviceData.subject.trim());
            }

            const randomIndex = Math.floor(Math.random() * AUTO_AI_SERVICE_TEMPLATE_IDS.length);
            const selectedTemplateId = AUTO_AI_SERVICE_TEMPLATE_IDS[randomIndex];

            toast({
                title: "🎨 Service Saved & Template Selected!",
                description: "Your service has been saved.",
                duration: 1000
            });

            if (onAutoAISelect) {
                onAutoAISelect(newService, selectedTemplateId);
            }
        } catch (error: any) {
            console.error("Error:", error);
            toast({
                title: "Error",
                description: error.response?.data?.error || error.response?.data?.message || "Failed to add service",
                variant: "destructive",
            });
        } finally {
            setIsAutoAIProcessing(false);
        }
    }, [
        getValidationItems,
        serviceData,
        photos,
        features,
        toast,
        setCampaignSubject,
        onAutoAISelect,
        getFullUrl,
        onServiceAdd,
        markAllRequiredTouched,
    ]);
    const validateEditServiceForm = useCallback(() => {
        if (!editForm) return false;

        const missingFields: string[] = [];

        if (!editForm.title?.trim()) missingFields.push("Service Title");
        if (!editForm.service_type?.trim()) missingFields.push("Service Type");
        if (!editForm.subject?.trim()) missingFields.push("Email Subject");
        if (!editForm.description?.trim()) missingFields.push("Description");
        // if (!editForm.address?.trim()) missingFields.push("Address");
        if (editPhotos.length < 1) missingFields.push("At least one photo");
        if (editFeatures.filter((f) => (f.feature_name || f.name || "").trim()).length < 1) {
            missingFields.push("At least one feature");
        }

        if (missingFields.length > 0) {
            toast({
                title: "Missing required information",
                description: `Please fill all required fields before saving. Missing: ${missingFields.join(", ")}.`,
                variant: "destructive",
                duration: 1000,
            });
            return false;
        }

        return true;
    }, [editForm, editPhotos.length, editFeatures, toast]);

    // ✅ FIXED: Update service with features
    const handleUpdateService = useCallback(async () => {
        if (!editForm) return;

        if (!validateEditServiceForm()) {
            return;
        }

        setIsSaving(true);

        try {
            const formData = new FormData();

            formData.append("title", editForm.title || "");
            formData.append("headline", editForm.title || "");
            formData.append("service_type", editForm.service_type || "");
            formData.append("description", editForm.description || "");

            if (editForm.subject) formData.append("subject", editForm.subject);
            if (editForm.phone) formData.append("phone", editForm.phone);
            if (editForm.email) formData.append("email", editForm.email);
            if (editForm.website) formData.append("website", editForm.website);
            if (editForm.address) formData.append("address", editForm.address);

            const validFeatures = editFeatures
                .filter((f) => (f.feature_name || f.name || "").trim().length > 0)
                .map((f) => ({ name: (f.feature_name || f.name || "").trim() }));
            formData.append("features", JSON.stringify(validFeatures));

            // ─── Photos ───────────────────────────────────────────────────────────
            const mainPhoto = editPhotos[0] ?? null;
            const secondaryPhotos = editPhotos.slice(1);

            // Hero: new File takes priority, else send existing URL as-is
            if (mainPhoto?.file instanceof File) {
                formData.append("hero_image", mainPhoto.file, mainPhoto.file.name);
            } else if (mainPhoto?.url && !mainPhoto.url.startsWith("blob:")) {
                formData.append("hero_image_url", mainPhoto.url);
            }

            // Secondaries: new Files go as photo_N, existing URLs go into photos_existing
            // ✅ CRITICAL: exclude hero URL from photos_existing to prevent backend swap-back
            const heroNorm = normalizePhotoPath(mainPhoto?.url ?? "");
            const secondaryUrls: string[] = [];

            for (let i = 0; i < secondaryPhotos.length; i++) {
                const photo = secondaryPhotos[i];
                if (!photo) continue;

                if (photo.file instanceof File) {
                    formData.append(`photo_${i + 1}`, photo.file, photo.file.name);
                } else if (photo.url && !photo.url.startsWith("blob:")) {
                    // Only add if it's genuinely different from the hero
                    if (normalizePhotoPath(photo.url) !== heroNorm) {
                        secondaryUrls.push(photo.url);
                    }
                }
            }

            // Always send (even empty array) so backend clears stale secondaries
            formData.append("photos_existing", JSON.stringify(secondaryUrls));

            if (!mainPhoto) {
                formData.append("hero_image_url", "");
                formData.append("remove_hero", "true");
            }

            // ─── PATCH ────────────────────────────────────────────────────────────
            await axiosInstance.patch(
                `${endpoints.service_listings}${editForm.id}/`,
                formData,
                {
                    headers: { "Content-Type": undefined },
                    transformRequest: [(data) => data],
                }
            );

            // ─── Fetch fresh server data ──────────────────────────────────────────
            const { data: serverData } = await axiosInstance.get(
                `${endpoints.service_listings}${editForm.id}/`
            );

            // ✅ KEY FIX: Build hero from LOCAL editPhotos order first.
            // The server may lag or return stale ordering — trust what the user set.
            let preferredHeroImage: string;
            if (mainPhoto?.file instanceof File) {
                // New file upload — use server's returned hero (has the real saved URL)
                const raw = serverData.hero_image_url || serverData.hero_image || "";
                preferredHeroImage = raw.startsWith("http") ? raw : getFullUrl(raw);
            } else {
                // Existing photo reordered — use the URL we already had (already absolute)
                preferredHeroImage = mainPhoto?.url ?? "";
                // Fallback to server if somehow empty
                if (!preferredHeroImage) {
                    const raw = serverData.hero_image_url || serverData.hero_image || "";
                    preferredHeroImage = raw.startsWith("http") ? raw : getFullUrl(raw);
                }
            }

            // ✅ Build secondaries from LOCAL editPhotos order (preserves user's arrangement)
            // Supplement with server data only for newly uploaded photos (which have real URLs now)
            const serverAllPhotos: string[] = (
                serverData.photos_urls || serverData.photos || []
            ).map((p: string) => (p.startsWith("http") ? p : getFullUrl(p)));

            const heroNormFinal = normalizePhotoPath(preferredHeroImage);

            // Start from local secondary order, replacing blob URLs with server URLs
            const preferredAdditionalPhotos: string[] = secondaryPhotos
                .map((photo, i) => {
                    if (photo.file instanceof File) {
                        // Find matching server URL for newly uploaded secondary
                        const match = serverAllPhotos.find(
                            (su) => normalizePhotoPath(su) !== heroNormFinal
                                && !secondaryUrls.some((eu) => normalizePhotoPath(eu) === normalizePhotoPath(su))
                        );
                        return match ?? "";
                    }
                    return photo.url ?? "";
                })
                .filter((url) =>
                    url &&
                    !url.startsWith("blob:") &&
                    normalizePhotoPath(url) !== heroNormFinal
                );

            const updatedService: SavedService = {
                id: String(serverData.id),
                title: serverData.title || editForm.title || "",
                service_type: serverData.service_type || editForm.service_type || "",
                description: serverData.description || editForm.description || "",
                phone: serverData.phone || editForm.phone || "",
                email: serverData.email || editForm.email || "",
                website: serverData.website || editForm.website || "",
                address: serverData.address || editForm.address || "",
                image: preferredHeroImage,
                hero_image_url: preferredHeroImage,
                photos: preferredAdditionalPhotos,
                photos_urls: preferredAdditionalPhotos,
                subject: serverData.subject || editForm.subject || "",
                // ✅ Normalize features — handle both name/feature_name from server
                features: (serverData.features || [])
                    .map((f: any) => ({
                        id: f.id,
                        name: f.name || f.feature_name || "",
                        feature_name: f.feature_name || f.name || "",
                    }))
                    .filter((f: any) => f.name.trim() !== ""),
            };

            // ✅ Update local state immediately
            setServiceList((prev) =>
                prev.map((s) =>
                    String(s.id) === String(updatedService.id) ? updatedService : s
                )
            );

            if (onServiceUpdate) onServiceUpdate(updatedService);

            // ✅ KEY FIX: Also update editService state so if user reopens,
            // the useEffect reads fresh data (no deleted photos reappearing)
            // We close the dialog here so set to null
            setEditService(null);
            setEditPhotos([]);
            setEditForm(null);
            setEditFeatures([]);
        } catch (error: any) {
            console.error("Update error:", error);
            toast({
                title: "❌ Error",
                description:
                    error.response?.data?.message ||
                    error.response?.data?.error ||
                    "Failed to update service",
                variant: "destructive",
            });
        } finally {
            setIsSaving(false);
        }
    }, [
        editForm,
        editPhotos,
        editFeatures,
        toast,
        onServiceUpdate,
        getFullUrl,
    ]);

    // ==========================================
    // RENDER COMPONENTS
    // ==========================================

    // Service Source Selector
    const ServiceSourceSelector = useMemo(() => (
        <div className="space-y-3">
            <Label className="text-base font-medium">
                How would you like to add a service?
            </Label>
            <RadioGroup
                value={serviceSource}
                onValueChange={handleSourceChange}
                className="grid w-full gap-3 sm:grid-cols-2"
            >
                {SERVICE_SOURCE_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = serviceSource === option.value;
                    return (
                        <label
                            key={option.value}
                            htmlFor={option.value}
                            className={`
                  relative flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 p-3 text-center transition-all duration-200
                  ${isSelected
                                    ? "border-primary bg-primary/5 shadow-sm"
                                    : "border-border bg-background hover:border-primary/40 hover:bg-muted/30"
                                }
                `}
                        >
                            <RadioGroupItem value={option.value} id={option.value} className="sr-only" />
                            <div
                                className={`
                    flex h-10 w-10 items-center justify-center rounded-lg transition-colors
                    ${isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}
                  `}
                            >
                                <Icon className="h-6 w-6" />
                            </div>
                            <div className="space-y-1">
                                <p className={`font-semibold leading-tight ${isSelected ? "text-primary" : "text-foreground"}`}>
                                    {option.label}
                                </p>
                                <p className="text-xs text-muted-foreground leading-snug">
                                    {option.description}
                                </p>
                            </div>
                            {isSelected && (
                                <div className="absolute right-2 top-2">
                                    <CheckCircle className="h-5 w-5 text-primary" />
                                </div>
                            )}
                        </label>
                    );
                })}
            </RadioGroup>
        </div>
    ), [serviceSource, handleSourceChange]);

    // Saved Services List
    const SavedServicesList = useMemo(() => (
        <div className="space-y-4">
            {loadingServices ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary mr-3" />
                    <p className="text-muted-foreground text-sm">Loading services...</p>
                </div>
            ) : serviceList.length === 0 ? (
                <div className="text-center py-12 bg-muted/20 rounded-lg border-2 border-dashed border-muted px-4">
                    <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-2 text-sm">No saved services found.</p>
                    <p className="text-xs text-muted-foreground">
                        Use "Add New Service" to create a service listing.
                    </p>
                </div>
            ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-0 sm:pr-2">
                    {serviceList.map((service) => {
                        const isAutoAILoading = autoAILoadingServiceId === service.id;
                        const mainImage = service.hero_image_url || service.image
                            ? getFullUrl(service.hero_image_url || service.image)
                            : null;

                        return (
                            <div
                                key={service.id}
                                className="group relative border border-border rounded-lg p-3 bg-background transition-all duration-200 ease-in-out hover:shadow-lg hover:border-primary/30"
                            >
                                <div className="flex flex-col gap-3">

                                    {/* TOP ROW: image + text info */}
                                    <div className="flex items-start gap-3 min-w-0">
                                        {mainImage ? (
                                            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                                                <img
                                                    src={mainImage}
                                                    alt={service.title}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).style.display = "none";
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                                                <Briefcase className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
                                            </div>
                                        )}

                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold text-primary text-sm sm:text-base mb-1 truncate">
                                                {service.title}
                                            </h3>

                                            <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                                <Badge variant="secondary" className="text-xs whitespace-nowrap">
                                                    {service.service_type}
                                                </Badge>
                                                {service.phone && (
                                                    <span className="flex items-center gap-1 text-xs text-muted-foreground min-w-0">
                                                        <Phone className="h-3 w-3 flex-shrink-0" />
                                                        <span className="truncate max-w-[100px] sm:max-w-[140px]">
                                                            {service.phone}
                                                        </span>
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex flex-wrap gap-1">
                                                {(service.photos?.length || 0) > 0 && (
                                                    <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs whitespace-nowrap">
                                                        📷 {(service.photos?.length || 0) + 1} photos
                                                    </span>
                                                )}

                                                {(() => {
                                                    const validFeatures = (service.features || []).filter(
                                                        (f: any) => (f.name || f.feature_name || "").trim() !== ""
                                                    );
                                                    return validFeatures.length > 0 ? (
                                                        <>
                                                            {validFeatures.slice(0, 2).map((f: any, idx: number) => (
                                                                <Badge
                                                                    key={f.id || `feat-${idx}`}
                                                                    variant="outline"
                                                                    className="text-xs whitespace-nowrap"
                                                                >
                                                                    {(f.name || f.feature_name || "").trim()}
                                                                </Badge>
                                                            ))}
                                                            {validFeatures.length > 2 && (
                                                                <Badge variant="outline" className="text-xs whitespace-nowrap">
                                                                    +{validFeatures.length - 2} more
                                                                </Badge>
                                                            )}
                                                        </>
                                                    ) : null;
                                                })()}
                                            </div>
                                        </div>
                                    </div>

                                    {/* ACTIONS ROW */}
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">

                                        {/* Delete + Edit icons */}
                                        <div className="flex gap-1.5 sm:flex-shrink-0">
                                            <Button
                                                size="icon"
                                                variant="outline"
                                                className="h-9 w-9 flex-shrink-0 border border-red-200 text-red-500 hover:bg-red-50 hover:text-red-700"
                                                onClick={(e) => { e.stopPropagation(); openDeleteDialog(service.id); }}
                                                aria-label="Delete service"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>

                                            <Button
                                                size="icon"
                                                variant="outline"
                                                className="h-9 w-9 flex-shrink-0 border border-gray-200 text-gray-600 hover:bg-gray-50"
                                                // In SavedServicesList, the edit button onClick:
                                                onClick={async () => {
                                                    try {
                                                        // ✅ Always fetch fresh from server when opening edit
                                                        const response = await axiosInstance.get(
                                                            `${endpoints.service_listings}${service.id}/`
                                                        );
                                                        // ✅ Merge server data with local serviceList data for photos
                                                        // (local list has the most up-to-date photo URLs after edits)
                                                        const localService = serviceList.find(
                                                            s => String(s.id) === String(service.id)
                                                        );
                                                        const freshData = {
                                                            ...response.data,
                                                            // Use local photo state which reflects latest edits
                                                            hero_image_url: localService?.hero_image_url || response.data.hero_image_url,
                                                            image: localService?.image || response.data.image,
                                                            photos: localService?.photos || response.data.photos || [],
                                                            photos_urls: localService?.photos_urls || response.data.photos_urls || [],
                                                        };
                                                        setEditService(freshData);
                                                    } catch (error) {
                                                        // Fallback to local data
                                                        const localService = serviceList.find(
                                                            s => String(s.id) === String(service.id)
                                                        );
                                                        setEditService(localService || service);
                                                    }
                                                }}
                                                aria-label="Edit service"
                                            >
                                                <Edit className="h-4 w-4" />
                                            </Button>
                                        </div>

                                        {/* Auto IQ + Use This Service */}
                                        <div className="flex flex-1 gap-1.5 min-w-0">
                                            <div className="relative group/autoiq flex-1 min-w-0">
                                                <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gradient-to-r from-red-500 to-red-600 px-2.5 py-1 text-xs font-semibold text-white opacity-0 group-hover/autoiq:opacity-100 transition-opacity duration-200 z-50">
                                                    Coming Soon
                                                </span>
                                                <Button
                                                    disabled={true}
                                                    className="h-9 w-full text-xs font-normal bg-gray-200 text-gray-500 border border-gray-200 shadow-none"
                                                >
                                                    {isAutoAILoading ? (
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                    ) : (
                                                        <span>✨ Auto IQ</span>
                                                    )}
                                                </Button>
                                            </div>

                                            <Button
                                                onClick={() => {
                                                    handleServiceSelect(service);
                                                    if (service.subject) setCampaignSubject(service.subject);
                                                }}
                                                className="h-9 flex-1 min-w-0 text-sm text-white bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 whitespace-nowrap"
                                            >
                                                Use This Service
                                            </Button>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    ), [serviceList, loadingServices, handleServiceSelect, setCampaignSubject, handleAutoAIClick, autoAILoadingServiceId, getFullUrl, openDeleteDialog]);
    // Key Features Section (for new service form)
    const KeyFeaturesSection = useMemo(() => (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <Label className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-primary" />
                        Key Features
                        <span className="text-destructive">*</span>
                    </Label>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Add up to {MAX_FEATURES} key features for your service
                    </p>
                </div>
                <span className={`text-sm font-medium ${features.length >= MAX_FEATURES ? "text-orange-600" : "text-muted-foreground"}`}>
                    {features.filter((f) => f.feature_name.trim()).length}/{MAX_FEATURES}
                </span>
            </div>

            <div className="space-y-2">
                {features.map((feature, index) => (
                    <div key={feature.id || `new-${index}`} className="flex items-center gap-3 group">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-xs font-medium text-primary">{index + 1}</span>
                        </div>

                        <Input
                            type="text"
                            value={feature.feature_name}
                            onChange={(e) => handleFeatureChange(index, e.target.value)}
                            onBlur={() => handleFeatureBlur(index)}
                            placeholder="e.g., 24/7 Support, Free Consultation... ( Max 30 Chars)"
                            className="flex-1"
                            maxLength={30}
                        />

                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleFeatureDelete(index)}
                            className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                ))}
            </div>

            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addFeature}
                disabled={features.length >= MAX_FEATURES}
                className={`w-full border-dashed ${touchedFields.has("features") && features.filter(f => f.feature_name.trim()).length === 0 ? "border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)] text-red-500 hover:text-red-600" : ""}`}
            >
                <Plus className="h-4 w-4 mr-2" />
                Add Feature
                {features.length >= MAX_FEATURES && (
                    <span className="ml-2 text-xs text-muted-foreground">(Limit reached)</span>
                )}
            </Button>

            {(() => {
                const allSuggestions = [
                    "Free Consultation",
                    "24/7 Support",
                    "Licensed & Insured",
                    "Satisfaction Guaranteed",
                    "Fast Response",
                    "Competitive Pricing",
                ];
                const addedFeatureNames = new Set(
                    features.map((f) => (f.feature_name || "").trim().toLowerCase())
                );
                const availableSuggestions = allSuggestions.filter(
                    (suggestion) => !addedFeatureNames.has(suggestion.toLowerCase())
                );

                return availableSuggestions.length > 0 ? (
                    <div className="p-4 bg-muted/30 rounded-lg border border-dashed">
                        <p className="text-sm text-muted-foreground mb-3">
                            💡 Suggested features to add:
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {availableSuggestions.map((suggestion) => (
                                <Button
                                    key={suggestion}
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        if (features.length < MAX_FEATURES) {
                                            setFeatures((prev) => [...prev, { feature_name: suggestion }]);
                                        }
                                    }}
                                    className="text-xs"
                                >
                                    + {suggestion}
                                </Button>
                            ))}
                        </div>
                    </div>
                ) : null;
            })()}
        </div>
    ), [features, addFeature, handleFeatureChange, handleFeatureBlur, handleFeatureDelete, touchedFields]);

    // ✅ NEW: Edit Features Section (for edit dialog)
    const EditKeyFeaturesSection = useMemo(() => (
        <div className="space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-border">
                <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    <h4 className="font-semibold text-foreground">Key Features</h4>
                </div>
                <span className={`text-sm font-medium ${editFeatures.length >= MAX_FEATURES ? "text-orange-600" : "text-muted-foreground"}`}>
                    {editFeatures.filter((f) => (f.feature_name || f.name || "").trim()).length}/{MAX_FEATURES}
                </span>
            </div>

            <div className="space-y-2">
                {editFeatures.map((feature, index) => (
                    <div key={feature.id || `edit-${index}`} className="flex items-center gap-3 group">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-xs font-medium text-primary">{index + 1}</span>
                        </div>

                        <Input
                            type="text"
                            value={feature.feature_name || feature.name || ""}
                            onChange={(e) => handleEditFeatureChange(index, e.target.value)}
                            onBlur={() => handleEditFeatureBlur(index)}
                            placeholder="e.g., 24/7 Support, Free Consultation..."
                            className="flex-1 h-10"
                        />

                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditFeatureDelete(index)}
                            className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                ))}
            </div>

            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addEditFeature}
                disabled={editFeatures.length >= MAX_FEATURES}
                className="w-full border-dashed"
            >
                <Plus className="h-4 w-4 mr-2" />
                Add Feature
                {editFeatures.length >= MAX_FEATURES && (
                    <span className="ml-2 text-xs text-muted-foreground">(Limit reached)</span>
                )}
            </Button>

            {(() => {
                const allSuggestions = [
                    "Free Consultation",
                    "24/7 Support",
                    "Licensed & Insured",
                    "Satisfaction Guaranteed",
                    "Fast Response",
                    "Competitive Pricing",
                ];
                const addedFeatureNames = new Set(
                    editFeatures.map((f) => (f.feature_name || f.name || "").trim().toLowerCase())
                );
                const availableSuggestions = allSuggestions.filter(
                    (suggestion) => !addedFeatureNames.has(suggestion.toLowerCase())
                );

                return availableSuggestions.length > 0 ? (
                    <div className="p-4 bg-muted/30 rounded-lg border border-dashed">
                        <p className="text-sm text-muted-foreground mb-3">
                            💡 Suggested features to add:
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {availableSuggestions.map((suggestion) => (
                                <Button
                                    key={suggestion}
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        if (editFeatures.length < MAX_FEATURES) {
                                            setEditFeatures((prev) => [...prev, { feature_name: suggestion, name: suggestion }]);
                                        }
                                    }}
                                    className="text-xs"
                                >
                                    + {suggestion}
                                </Button>
                            ))}
                        </div>
                    </div>
                ) : null;
            })()}
        </div>
    ), [editFeatures, addEditFeature, handleEditFeatureChange, handleEditFeatureBlur, handleEditFeatureDelete]);

    // Service Form
    const ServiceForm = useMemo(() => (
        <div className="space-y-6">
            <div className="grid gap-6 lg:grid-cols-2">
                {/* LEFT COLUMN */}
                <div className="space-y-4">
                    {/* Service Title */}
                    <div className="space-y-2">
                        <Label htmlFor="title" className="flex items-center gap-2">
                            Service Title
                            <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="title"
                            type="text"
                            placeholder="e.g., Professional AC Repair Service"
                            value={serviceData.title}
                            onChange={(e) => handleInputChange("title", e.target.value)}
                            onBlur={() => markTouched("title")}
                            autoComplete="off"
                            className={fieldError("title", serviceData.title) ? "border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]" : ""}
                        />
                    </div>

                    {/* Service Type */}
                    <div className="space-y-2">
                        <Label htmlFor="serviceType" className="flex items-center gap-2">
                            Service Type
                            <span className="text-destructive">*</span>
                        </Label>

                        <Select
                            value={getServiceTypeSelectValue()}
                            onValueChange={(v) => { handleServiceTypeChange(v); markTouched("serviceType"); }}
                        >
                            <SelectTrigger
                                id="serviceType"
                                onBlur={() => markTouched("serviceType")}
                                className={touchedFields.has("serviceType") && !isValidServiceType() ? "border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]" : ""}
                            >
                                <SelectValue placeholder="Select service type" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50">
                                    Popular Services
                                </div>
                                {SERVICE_TYPES.map((type) => (
                                    <SelectItem key={type} value={type} className="cursor-pointer">
                                        {type}
                                    </SelectItem>
                                ))}
                                <div className="my-2 border-t border-border/50" />
                                <SelectItem value="custom" className="cursor-pointer text-primary font-medium">
                                    <div className="flex items-center gap-2">
                                        <Pencil className="h-4 w-4" />
                                        <span>Other (Custom Service)</span>
                                    </div>
                                </SelectItem>
                            </SelectContent>
                        </Select>

                        {showCustomServiceType && (
                            <div className="animate-in slide-in-from-top-2 duration-300 space-y-3">
                                <div className="relative">
                                    <Input
                                        id="custom-service-type"
                                        placeholder="Enter your custom service type..."
                                        value={customServiceType}
                                        onChange={handleCustomServiceTypeInput}
                                        onBlur={() => markTouched("serviceType")}
                                        className={`pr-10${touchedFields.has("serviceType") && !customServiceType.trim() ? " border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]" : ""}`}
                                        autoFocus
                                    />
                                    <Pencil className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-primary/50" />
                                </div>

                                {customServiceType.trim() && (
                                    <div className="flex items-center gap-2 animate-in fade-in duration-200">
                                        <span className="text-xs text-muted-foreground">Selected:</span>
                                        <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                                            <CheckCircle2 className="h-3 w-3 mr-1" />
                                            {customServiceType}
                                        </Badge>
                                    </div>
                                )}
                            </div>
                        )}

                        {!showCustomServiceType && serviceData.serviceType && (
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">Selected:</span>
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    {serviceData.serviceType}
                                </Badge>
                            </div>
                        )}
                    </div>

                    {/* Email Subject */}
                    <div className="space-y-2">
                        <Label htmlFor="subject" className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-primary" />
                            Email Subject
                            <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="subject"
                            placeholder="e.g., Exclusive Mortgage Deals Just for You"
                            value={serviceData.subject}
                            onChange={(e) => handleInputChange("subject", e.target.value)}
                            onBlur={() => markTouched("subject")}
                            className={fieldError("subject", serviceData.subject) ? "border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]" : ""}
                        />
                        <p className="text-xs text-muted-foreground">
                            This subject will be used in your email launch.
                        </p>
                    </div>

                </div>

                {/* RIGHT COLUMN */}
                <div className="space-y-6">
                    {/* Description */}
                    <div className="space-y-2">
                        <Label htmlFor="description" className="flex items-center gap-2">
                            Description
                            <span className="text-destructive">*</span>
                        </Label>
                        <Textarea
                            id="description"
                            rows={8}
                            placeholder={`Describe your service in detail...

• What problems do you solve?
• What makes your service unique?
• What can customers expect?`}
                            value={serviceData.description}
                            onChange={(e) => handleInputChange("description", e.target.value)}
                            onBlur={() => markTouched("description")}
                            className={`resize-none whitespace-pre-line${fieldError("description", serviceData.description) ? " border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]" : ""}`}
                        />

                        {/* Character count */}
                        <div className="flex justify-between items-center">
                            <div className="flex flex-wrap gap-2">
                                <div className="relative group">
                                    {/* <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gradient-to-r from-red-500 to-red-600 px-2.5 py-1 text-xs font-semibold text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50">
    Coming Soon
  </span> */}
                                    <Button
                                        type="button"
                                        size="sm"
                                        onClick={handleAutoIQ}
                                        disabled={
                                            isAutoIQLoading ||
                                            autoIQCount >= MAX_AUTO_IQ_ATTEMPTS ||
                                            serviceData.description.trim().length < 10
                                        }
                                        className="bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 hover:from-blue-500 hover:via-blue-600 hover:to-blue-700 text-white"
                                    >
                                        {isAutoIQLoading ? (
                                            <>
                                                <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                                                Thinking...
                                            </>
                                        ) : autoIQCount >= MAX_AUTO_IQ_ATTEMPTS ? (
                                            "Limit Reached"
                                        ) : (
                                            <>
                                                <Sparkles className="h-3 w-3 mr-1.5" />
                                                Auto IQ
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>

                            <span
                                className={`text-xs font-medium px-2 py-1 rounded-full ${(serviceData.description?.length || 0) > 50
                                    ? "bg-green-100 text-green-700"
                                    : "bg-muted text-muted-foreground"
                                    }`}
                            >
                                {serviceData.description?.length || 0} characters
                            </span>
                        </div>

                        <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
                            <Lightbulb className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-700 leading-relaxed">
                                💡 Type at least 10 characters to enable AI features. Click
                                <span className="font-semibold mx-1">Auto IQ</span>
                                for an AI-powered description and subject line.
                                {autoIQCount > 0 && (
                                    <span className="block mt-1 text-blue-600">
                                        ✨ Used {autoIQCount}/{MAX_AUTO_IQ_ATTEMPTS} times
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* KEY FEATURES SECTION - Full Width */}
            <div className="border-t pt-6">{KeyFeaturesSection}</div>
        </div>
    ), [
        serviceData,
        showCustomServiceType,
        customServiceType,
        isAutoIQLoading,
        autoIQCount,
        handleInputChange,
        handleServiceTypeChange,
        handleCustomServiceTypeInput,
        getServiceTypeSelectValue,
        handleAutoIQ,
        KeyFeaturesSection,
        markTouched,
        fieldError,
        touchedFields,
        isValidServiceType,
    ]);

    const ImageUploadSection = useMemo(() => {
        const canAddMore = photos.length < MAX_PHOTOS;

        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <Label className="flex items-center gap-2">
                            Service Images
                            <span className="text-destructive">*</span>
                        </Label>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Upload up to {MAX_PHOTOS} photos. First photo will be the main image.
                        </p>
                    </div>
                    <span className={`text-sm font-medium ${photos.length >= 1 ? "text-green-600" : "text-muted-foreground"}`}>
                        {photos.length}/{MAX_PHOTOS} photos
                    </span>
                </div>

                {canAddMore && (
                    <div
                        ref={dropZoneRef}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`
                  relative cursor-pointer rounded-lg border border-dashed p-8
                  transition-all duration-200 ease-in-out
                  ${isDragging
                                ? "border-primary bg-primary/5 scale-[1.02]"
                                : touchedFields.has("photos") && photos.length === 0
                                    ? "border-red-300 bg-red-50/30 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]"
                                    : "border-border bg-background hover:border-primary/50 hover:bg-muted/40"
                            }
                `}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleFileInputChange}
                            className="hidden"
                        />
                        <div className="flex flex-col items-center justify-center text-center">
                            <div className={`mb-4 rounded-full p-4 transition-colors ${isDragging ? "bg-primary/10" : "bg-muted"}`}>
                                <Upload className={`h-8 w-8 ${isDragging ? "text-primary" : "text-muted-foreground"}`} />
                            </div>
                            <p className="text-base font-medium text-foreground">
                                {isDragging ? "Drop your photos here" : "Drag & drop photos here"}
                            </p>
                            <p className="mt-1 text-sm text-muted-foreground">
                                or <span className="font-medium text-primary">click to browse</span>
                            </p>
                            <p className="mt-3 text-xs text-muted-foreground">
                                PNG, JPG up to 5 MB each • Max {MAX_PHOTOS - photos.length} more
                            </p>
                        </div>
                    </div>
                )}

                {photos.length > 0 && (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <Label className="text-sm text-muted-foreground">Uploaded Photos</Label>
                            <p className="text-xs text-muted-foreground">Hover on image for enhance</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            {photos.map((photo, index) => {
                                const isEnhanced = enhancedPhotoIds.has(photo.id);

                                return (
                                    <div
                                        key={photo.id}
                                        className={`
                          group relative overflow-hidden rounded-xl transition-all duration-300
                          ${index === 0
                                                ? "ring-2 ring-primary ring-offset-2 shadow-lg shadow-primary/20"
                                                : "ring-1 ring-border hover:ring-2 hover:ring-primary/50 hover:shadow-md"
                                            }
                        `}
                                    >
                                        {index === 0 && (
                                            <div className="absolute left-2 top-2 z-20 flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground shadow-lg">
                                                <CheckCircle2 className="h-3 w-3" />
                                                Main
                                            </div>
                                        )}

                                        {isEnhanced && (
                                            <div className="absolute left-2 bottom-2 z-20 flex items-center gap-1 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 px-2.5 py-1 text-xs font-semibold text-white shadow-lg">
                                                <Sparkles className="h-3 w-3" />
                                                Enhanced
                                            </div>
                                        )}

                                        <div className="absolute right-10 top-2 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-xs font-bold text-white backdrop-blur-sm">
                                            {index + 1}
                                        </div>

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removePhoto(photo.id);
                                            }}
                                            className="absolute right-2 top-2 z-30 flex h-7 w-7 items-center justify-center rounded-full bg-red-500 text-white shadow-lg ring-2 ring-white/80 transition-all duration-200 hover:bg-red-600 hover:scale-110 hover:ring-white active:scale-95"
                                            title="Remove photo"
                                        >
                                            <X className="h-4 w-4" strokeWidth={2.5} />
                                        </button>

                                        <div className="aspect-[4/3] bg-muted">
                                            <img
                                                src={photo.url}
                                                alt={`Service photo ${index + 1}`}
                                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                                loading="lazy"
                                            />
                                        </div>

                                        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-gradient-to-t from-black/80 via-black/50 to-transparent opacity-0 transition-all duration-300 group-hover:opacity-100">
                                            {photo.file && !isEnhanced && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEnhanceImage(photo);
                                                    }}
                                                    className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all duration-200 hover:from-blue-600 hover:to-indigo-700 hover:scale-105 hover:shadow-xl active:scale-95"
                                                >
                                                    <Sparkles className="h-4 w-4" />
                                                    Auto AI
                                                </button>
                                            )}

                                            {index !== 0 && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setAsMainPhoto(photo.id);
                                                    }}
                                                    className="flex items-center gap-2 rounded-lg bg-white/20 backdrop-blur-md px-4 py-2 text-sm font-medium text-white border border-white/30 transition-all duration-200 hover:bg-white/30 hover:scale-105 active:scale-95"
                                                >
                                                    <ImageIcon className="h-4 w-4" />
                                                    Set as Main
                                                </button>
                                            )}
                                        </div>

                                        {index === 0 && (
                                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary to-primary/60" />
                                        )}
                                    </div>
                                );
                            })}

                            {canAddMore && (
                                <div
                                    onClick={() => fileInputRef.current?.click()}
                                    className="flex aspect-[4/3] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-muted/30 transition-all hover:border-primary/50 hover:bg-muted/50"
                                >
                                    <Plus className="mb-1 h-6 w-6 text-muted-foreground" />
                                    <span className="text-xs font-medium text-muted-foreground">Add Photo</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }, [
        photos,
        isDragging,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleFileInputChange,
        removePhoto,
        setAsMainPhoto,
        enhancedPhotoIds,
        handleEnhanceImage,
        touchedFields,
    ]);

    // Validation Section
    const ValidationSection = useMemo(() => {
        const validationItems = getValidationItems();
        const completedCount = validationItems.filter((item) => item.isValid).length;
        const totalCount = validationItems.length;
        const isComplete = completedCount === totalCount;

        return (
            <div className={`rounded-lg border p-4 transition-colors ${isComplete ? "border-green-200 bg-green-50" : "border-border bg-muted/30"}`}>
                <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-primary">Validation Status</h4>
                    <span className={`text-sm font-medium ${isComplete ? "text-green-600" : "text-muted-foreground"}`}>
                        {completedCount}/{totalCount} complete
                    </span>
                </div>
                <div className="grid gap-2 text-sm sm:grid-cols-2">
                    {validationItems.map((item, index) => (
                        <div key={index} className="flex items-center gap-2">
                            {item.isValid ? (
                                <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
                            ) : (
                                <AlertCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
                            )}
                            <span className={item.isValid ? "text-foreground" : "text-muted-foreground"}>
                                {item.label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }, [getValidationItems]);

    // Enhancement Dialog
    const EnhancementDialog = useMemo(() => (
        <Dialog
            open={enhanceDialogOpen}
            onOpenChange={(open) => {
                if (!open) {
                    setEnhanceDialogOpen(false);
                    setEnhanceDialogPhoto(null);
                    setOriginalImageUrl(null);
                    setEnhancedPreviewUrl(null);
                    setIsEnhancingPreview(false);
                }
            }}
        >
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0">
                <DialogHeader className="sticky top-0 z-10 bg-background border-b px-6 py-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                            <Sparkles className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <DialogTitle className="text-xl font-semibold">AI Image Enhancement Preview</DialogTitle>
                            <p className="text-sm text-muted-foreground mt-0.5">
                                Compare before and after AI enhancement
                            </p>
                        </div>
                    </div>
                </DialogHeader>

                <div className="p-6 space-y-6">
                    {isEnhancingPreview ? (
                        <div className="flex flex-col items-center justify-center py-16">
                            <div className="relative">
                                <div className="h-20 w-20 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <Sparkles className="h-8 w-8 text-blue-600 animate-pulse" />
                                </div>
                            </div>
                            <p className="mt-6 text-lg font-medium text-foreground">Enhancing your image...</p>
                            <p className="mt-2 text-sm text-muted-foreground">This may take a few seconds</p>

                            {originalImageUrl && (
                                <div className="mt-6 p-2 bg-muted rounded-lg">
                                    <img
                                        src={originalImageUrl}
                                        alt="Processing"
                                        className="w-32 h-24 object-cover rounded opacity-60"
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Original Image */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-semibold text-foreground flex items-center gap-2">
                                            <div className="h-3 w-3 rounded-full bg-gray-400" />
                                            Original Image
                                        </h3>
                                    </div>
                                    <div className="relative rounded-xl overflow-hidden border-2 border-gray-300 bg-muted min-h-[200px]">
                                        {originalImageUrl ? (
                                            <>
                                                <img
                                                    src={originalImageUrl}
                                                    alt="Original"
                                                    className="w-full h-auto max-h-[450px] object-contain"
                                                />
                                                <div className="absolute top-3 left-3 bg-gray-600 text-white px-3 py-1.5 rounded-full text-xs font-semibold">
                                                    Before
                                                </div>
                                            </>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
                                                <AlertCircle className="h-16 w-16 mb-3 opacity-30" />
                                                <p className="text-sm">Original image not available</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Enhanced Image */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-semibold text-foreground flex items-center gap-2">
                                            <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
                                            Enhanced Image
                                        </h3>
                                        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                                            <Sparkles className="h-3 w-3" />
                                            AI Enhanced
                                        </span>
                                    </div>
                                    <div className="relative rounded-xl overflow-hidden border-2 border-green-500 bg-muted shadow-lg shadow-green-500/20 min-h-[200px]">
                                        {enhancedPreviewUrl ? (
                                            <>
                                                <img
                                                    src={enhancedPreviewUrl}
                                                    alt="Enhanced"
                                                    className="w-full h-auto max-h-[450px] object-contain"
                                                />
                                                <div className="absolute top-3 left-3 bg-green-600 text-white px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5">
                                                    <Sparkles className="h-3 w-3" />
                                                    After
                                                </div>
                                            </>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
                                                <AlertCircle className="h-16 w-16 mb-3 opacity-30" />
                                                <p className="text-sm">Enhancement preview not available</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Enhancement Info */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border border-blue-200 dark:border-blue-800 rounded-xl p-5">
                                <div className="flex items-start gap-4">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 flex-shrink-0">
                                        <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                                            AI Enhancement Applied
                                        </h4>
                                        <div className="grid grid-cols-2 gap-2 text-sm text-blue-700 dark:text-blue-300">
                                            <div className="flex items-center gap-2">
                                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                                <span>Improved lighting & contrast</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                                <span>Enhanced color accuracy</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                                <span>Reduced noise & artifacts</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                                <span>Sharpened details</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t">
                                <Button
                                    variant="outline"
                                    size="lg"
                                    onClick={() => {
                                        setEnhanceDialogOpen(false);
                                        setEnhanceDialogPhoto(null);
                                        setOriginalImageUrl(null);
                                        setEnhancedPreviewUrl(null);
                                    }}
                                    className="px-6"
                                >
                                    Cancel
                                </Button>

                                <Button
                                    size="lg"
                                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-8"
                                    onClick={handleApplyEnhancement}
                                    disabled={!enhancedPreviewUrl}
                                >
                                    <Sparkles className="mr-2 h-4 w-4" />
                                    Apply Enhancement
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    ), [
        enhanceDialogOpen,
        enhanceDialogPhoto,
        originalImageUrl,
        enhancedPreviewUrl,
        isEnhancingPreview,
        handleApplyEnhancement,
    ]);

    // Delete Confirmation Dialog
    const DeleteConfirmationDialog = useMemo(() => (
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Trash2 className="h-5 w-5 text-red-600" />
                        Delete Service
                    </DialogTitle>
                    <DialogDescription className="text-base">
                        Are you sure you want to delete this service? This action cannot be undone.
                        The service and all its photos will be permanently removed.
                    </DialogDescription>
                </DialogHeader>

                <DialogFooter className="gap-2 sm:justify-between">
                    <Button variant="outline" onClick={closeDeleteDialog} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button variant="destructive" onClick={confirmDelete} disabled={deleting}>
                        {deleting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Deleting...
                            </>
                        ) : (
                            "Delete"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    ), [deleteDialogOpen, deleting, closeDeleteDialog, confirmDelete]);

    // ✅ FIXED: Edit Key Features Section for Edit Dialog
    //   const EditKeyFeaturesSection = useMemo(() => (
    //     <div className="space-y-4">
    //       <div className="flex items-center justify-between pb-2 border-b border-border">
    //         <div className="flex items-center gap-2">
    //           <Sparkles className="h-5 w-5 text-primary" />
    //           <h4 className="font-semibold text-foreground">Key Features</h4>
    //         </div>
    //         <span className={`text-sm font-medium ${editFeatures.length >= MAX_FEATURES ? "text-orange-600" : "text-muted-foreground"}`}>
    //           {editFeatures.filter((f) => (f.feature_name || f.name || "").trim()).length}/{MAX_FEATURES}
    //         </span>
    //       </div>

    //       <div className="space-y-2">
    //         {editFeatures.map((feature, index) => (
    //           <div key={feature.id || `edit-feature-${index}`} className="flex items-center gap-3 group">
    //             <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
    //               <span className="text-xs font-medium text-primary">{index + 1}</span>
    //             </div>

    //             <Input
    //               type="text"
    //               value={feature.feature_name || feature.name || ""}
    //               onChange={(e) => handleEditFeatureChange(index, e.target.value)}
    //               onBlur={() => handleEditFeatureBlur(index)}
    //               placeholder="e.g., 24/7 Support, Free Consultation..."
    //               className="flex-1 h-10"
    //             />

    //             <Button
    //               type="button"
    //               variant="ghost"
    //               size="icon"
    //               onClick={() => handleEditFeatureDelete(index)}
    //               className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50"
    //             >
    //               <X className="h-4 w-4" />
    //             </Button>
    //           </div>
    //         ))}
    //       </div>

    //       <Button
    //         type="button"
    //         variant="outline"
    //         size="sm"
    //         onClick={addEditFeature}
    //         disabled={editFeatures.length >= MAX_FEATURES}
    //         className="w-full border-dashed"
    //       >
    //         <Plus className="h-4 w-4 mr-2" />
    //         Add Feature
    //         {editFeatures.length >= MAX_FEATURES && (
    //           <span className="ml-2 text-xs text-muted-foreground">(Limit reached)</span>
    //         )}
    //       </Button>

    //       {editFeatures.length === 0 && (
    //         <div className="p-4 bg-muted/30 rounded-lg border border-dashed">
    //           <p className="text-sm text-muted-foreground mb-3">
    //             💡 Suggested features to add:
    //           </p>
    //           <div className="flex flex-wrap gap-2">
    //             {[
    //               "Free Consultation",
    //               "24/7 Support",
    //               "Licensed & Insured",
    //               "Satisfaction Guaranteed",
    //               "Fast Response",
    //               "Competitive Pricing",
    //             ].map((suggestion) => (
    //               <Button
    //                 key={suggestion}
    //                 type="button"
    //                 variant="secondary"
    //                 size="sm"
    //                 onClick={() => {
    //                   if (editFeatures.length < MAX_FEATURES) {
    //                     setEditFeatures((prev) => [...prev, { feature_name: suggestion, name: suggestion }]);
    //                   }
    //                 }}
    //                 className="text-xs"
    //               >
    //                 + {suggestion}
    //               </Button>
    //             ))}
    //           </div>
    //         </div>
    //       )}
    //     </div>
    //   ), [editFeatures, addEditFeature, handleEditFeatureChange, handleEditFeatureBlur, handleEditFeatureDelete]);

    // ✅ FIXED: Edit Service Dialog with Subject and Features
    const EditServiceDialog = useMemo(() => (
        <Dialog
            open={!!editService}
            onOpenChange={(open) => {
                if (!open) {
                    setEditService(null);
                    setEditPhotos([]);
                    setEditForm(null);
                    setEditFeatures([]);
                }
            }}
        >
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}>
                <DialogHeader className="sticky top-0 z-10 bg-background border-b px-6 py-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                            <Edit className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                            <DialogTitle className="text-xl font-semibold">Edit Service</DialogTitle>
                            <p className="text-sm text-muted-foreground mt-0.5">
                                Update all service information
                            </p>
                        </div>
                    </div>
                </DialogHeader>

                {editForm && (
                    <div className="p-6 space-y-8">
                        {/* ✅ Email Subject Line Section */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 pb-2 border-b border-border">
                                <Mail className="h-5 w-5 text-primary" />
                                <h4 className="font-semibold text-foreground">Email Subject Line</h4>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="edit-subject" className="text-sm font-medium">
                                    Subject <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    id="edit-subject"
                                    placeholder="Enter email subject line..."
                                    value={editForm.subject || ""}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setEditForm(prev => prev ? { ...prev, subject: newValue } : null);
                                    }}
                                    className="h-11"
                                />
                                <p className="text-xs text-muted-foreground">
                                    This subject line will be used when sending launch emails
                                </p>
                            </div>
                        </div>

                        {/* Basic Information Section */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 pb-2 border-b border-border">
                                <Briefcase className="h-5 w-5 text-primary" />
                                <h4 className="font-semibold text-foreground">Basic Information</h4>
                            </div>

                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="space-y-2">
                                    <Label htmlFor="edit-title" className="text-sm font-medium">
                                        Service Title <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        id="edit-title"
                                        placeholder="Enter service title..."
                                        value={editForm.title || ""}
                                        onChange={(e) => setEditForm(prev => prev ? { ...prev, title: e.target.value } : null)}
                                        className="h-11"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="edit-service-type" className="text-sm font-medium">
                                        Service Type <span className="text-red-500">*</span>
                                    </Label>
                                    <Select
                                        value={editForm.service_type || ""}
                                        onValueChange={(value) => setEditForm(prev => prev ? { ...prev, service_type: value } : null)}
                                    >
                                        <SelectTrigger id="edit-service-type" className="h-11">
                                            <SelectValue placeholder="Select type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {SERVICE_TYPES.map((type) => (
                                                <SelectItem key={type} value={type}>
                                                    {type}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>

                        {/* Contact Information Section */}
                        <div className="space-y-4">
                            {/* <div className="flex items-center gap-2 pb-2 border-b border-border">
                                <Phone className="h-5 w-5 text-primary" />
                                <h4 className="font-semibold text-foreground">Contact Information</h4>
                            </div> */}

                            <div className="grid grid-cols-2 gap-4">
                               {/* <div className="space-y-2">
    <Label htmlFor="edit-phone" className="text-sm font-medium">
        Phone
    </Label>
    <div className="relative">
        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
            id="edit-phone"
            type="tel"
            value={editForm.phone || ""}
            readOnly
            className="h-11 pl-10 bg-muted cursor-not-allowed select-none focus-visible:ring-0 focus-visible:ring-offset-0"
        />
    </div>
</div> */}

                                {/* <div className="space-y-2">
    <Label htmlFor="edit-email" className="text-sm font-medium">
        Email
    </Label>
    <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
            id="edit-email"
            type="email"
            value={editForm.email || ""}
            readOnly
            className="h-11 pl-10 bg-muted cursor-not-allowed select-none focus-visible:ring-0 focus-visible:ring-offset-0"
        />
    </div>
</div> */}

                                <div className="space-y-2">
                                    <Label htmlFor="edit-website" className="text-sm font-medium">
                                        Website
                                    </Label>
                                    <div className="relative">
                                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="edit-website"
                                            type="url"
                                            placeholder="www.yourwebsite.com"
                                            value={editForm.website || ""}
                                            onChange={(e) => setEditForm(prev => prev ? { ...prev, website: e.target.value } : null)}
                                            className="h-11 pl-10"
                                        />
                                    </div>
                                </div>

                                {/* <div className="space-y-2">
                                    <Label htmlFor="edit-address" className="text-sm font-medium">
                                        Address <span className="text-red-500">*</span>
                                    </Label>
                                    <div className="relative">
                                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="edit-address"
                                            placeholder="123 Main Street, City, State"
                                            value={editForm.address || ""}
                                            onChange={(e) => setEditForm(prev => prev ? { ...prev, address: e.target.value } : null)}
                                            className="h-11 pl-10"
                                        />
                                    </div>
                                </div> */}
                            </div>
                        </div>

                        {/* Description Section */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 pb-2 border-b border-border">
                                <Info className="h-5 w-5 text-primary" />
                                <h4 className="font-semibold text-foreground">Service Description</h4>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="edit-description" className="text-sm font-medium">
                                    Description <span className="text-red-500">*</span>
                                </Label>
                                <Textarea
                                    id="edit-description"
                                    rows={6}
                                    required
                                    placeholder="Enter service description..."
                                    value={editForm.description || ""}
                                    onChange={(e) => setEditForm(prev => prev ? { ...prev, description: e.target.value } : null)}
                                    className="resize-none"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Provide a detailed description of your service offerings and highlights
                                </p>
                            </div>
                        </div>

                        {/* ✅ Key Features Section */}
                        {EditKeyFeaturesSection}

                        {/* Photos Section */}
                        {/* Photos Section - FIXED */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 pb-2 border-b border-border">
                                <ImageIcon className="h-5 w-5 text-primary" />
                                <h4 className="font-semibold text-foreground">Service Photos</h4>
                                <span className="ml-auto text-sm text-muted-foreground">
                                    {editPhotos.length}/{MAX_PHOTOS}
                                </span>
                            </div>

                            <p className="text-xs text-muted-foreground">
                                Click &quot;Set as Main&quot; to change the primary photo. The first photo is used as the hero image.
                            </p>

                            {/* Photo Grid — same pattern as Edit Property */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                {editPhotos.map((photo, index) => (
                                    <div
                                        key={photo.id}
                                        className={`
                                            group relative overflow-hidden rounded-lg transition-all duration-300
                                            ${index === 0
                                                ? "ring-2 ring-primary ring-offset-2 shadow-md"
                                                : "ring-1 ring-border hover:ring-2 hover:ring-primary/50"
                                            }
                                        `}
                                    >
                                        {index === 0 && (
                                            <div className="absolute left-2 top-2 z-20 flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground shadow-md">
                                                <CheckCircle2 className="h-3 w-3" />
                                                Main
                                            </div>
                                        )}

                                        <div className="absolute right-8 top-2 z-20 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-xs font-bold text-white">
                                            {index + 1}
                                        </div>

                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeEditPhoto(photo.id);
                                            }}
                                            className="absolute right-2 top-2 z-30 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white shadow-md transition-all hover:bg-red-600 hover:scale-110"
                                            title="Remove photo"
                                        >
                                            <X className="h-3 w-3" strokeWidth={2.5} />
                                        </button>

                                        <div className="aspect-[4/3] bg-muted">
                                            <img
                                                src={photo.url}
                                                alt={`Service photo ${index + 1}`}
                                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                                loading="lazy"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).style.display = "none";
                                                }}
                                            />
                                        </div>

                                        {photo.isExisting && index !== 0 && (
                                            <div className="absolute bottom-2 left-2 z-10 rounded bg-blue-500/90 px-1.5 py-0.5 text-[10px] font-medium text-white">
                                                Existing
                                            </div>
                                        )}
                                        {photo.file && (
                                            <div className="absolute bottom-2 left-2 z-10 rounded bg-green-500/90 px-1.5 py-0.5 text-[10px] font-medium text-white">
                                                New
                                            </div>
                                        )}

                                        {index !== 0 && (
                                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditAsMainPhoto(photo.id);
                                                    }}
                                                    className="flex items-center gap-2 rounded-md bg-white/90 px-3 py-1.5 text-sm font-medium text-foreground transition-all hover:bg-white"
                                                >
                                                    <ImageIcon className="h-3 w-3" />
                                                    Set as Main
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {editPhotos.length < MAX_PHOTOS && (
                                    <div
                                        ref={editDropZoneRef}
                                        onClick={() => editFileInputRef.current?.click()}
                                        onDragEnter={handleEditDragEnter}
                                        onDragLeave={handleEditDragLeave}
                                        onDragOver={handleEditDragOver}
                                        onDrop={handleEditDrop}
                                        className={`
                                            flex aspect-[4/3] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-all
                                            ${isEditDragging ? "border-primary bg-primary/10" : "border-border bg-muted/30 hover:border-primary/50 hover:bg-muted/50"}
                                        `}
                                    >
                                        <Plus className="mb-1 h-5 w-5 text-muted-foreground" />
                                        <span className="text-xs font-medium text-muted-foreground">Add Photo</span>
                                        <span className="text-[10px] text-muted-foreground mt-0.5">
                                            {editPhotos.length}/{MAX_PHOTOS}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <input
                                ref={editFileInputRef}
                                type="file"
                                accept="image/jpeg,image/png"
                                multiple
                                className="hidden"
                                onChange={handleEditFileInputChange}
                            />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-3 pt-6 border-t">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setEditService(null);
                                    setEditPhotos([]);
                                    setEditForm(null);
                                    setEditFeatures([]);
                                }}
                                size="lg"
                            >
                                Cancel
                            </Button>


                            <Button
                                className="bg-primary text-white hover:bg-primary/90"
                                onClick={handleUpdateService}
                                size="lg"
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 className="mr-2 h-4 w-4" />
                                        Save Changes
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    ), [
        editService,
        editForm,
        editPhotos,
        editFeatures,
        isSaving,
        isEditDragging,
        handleEditDragEnter,
        handleEditDragOver,
        handleEditDragLeave,
        handleEditDrop,
        handleEditFileInputChange,
        removeEditPhoto,
        setEditAsMainPhoto,
        handleUpdateService,
        EditKeyFeaturesSection,
    ]);

    // ==========================================
    // MAIN RENDER
    // ==========================================
    return (
        <div className="mx-auto w-full max-w-6xl px-4">
            {/* <h2 className="text-2xl font-bold text-primary mb-8 text-center">
                Select or Add a Service
            </h2> */}

            <Card className="w-full">
                <CardContent className="space-y-6 p-4 pt-6">
                    {/* Service Source Selector */}
                    {ServiceSourceSelector}

                    <hr className="border-border" />

                    {/* Saved Services */}
                    {serviceSource === "saved" && SavedServicesList}

                    {/* Manual Entry */}
                    {serviceSource === "manual" && (
                        <>
                            {ServiceForm}
                            {ImageUploadSection}
                            {ValidationSection}

                            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleResetForm}
                                    disabled={isSaving || isAutoAIProcessing}
                                >
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Reset
                                </Button>

                                {/* AUTO IQ Button for Manual Entry */}
                                <div className="relative group">
                                    <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gradient-to-r from-red-500 to-red-600 px-2.5 py-1 text-xs font-semibold text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50">
                                        Coming Soon
                                    </span>
                                    <Button
                                        type="button"
                                        size="lg"
                                        disabled={true}
                                        className="bg-gray-200 text-gray-700 hover:bg-gray-300 border border-gray-200 shadow-none font-normal"
                                    >
                                        <Sparkles className="mr-2 h-4 w-4" />
                                        Auto IQ
                                    </Button>
                                </div>

                                <Button
                                    type="button"
                                    variant="default"
                                    size="lg"
                                    onClick={handleAddServiceAndSelect}
                                    disabled={isSaving || isAutoAIProcessing}
                                    className="bg-primary hover:bg-primary/90"
                                >
                                    {isSaving ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Plus className="mr-2 h-4 w-4" />
                                            Add & Use This Service
                                        </>
                                    )}
                                </Button>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Back Button */}
            <div className="flex justify-start mt-6">
                <Button variant="outline" onClick={handleBack}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                </Button>
            </div>

            {/* Dialogs */}
            {EnhancementDialog}
            {DeleteConfirmationDialog}
            {EditServiceDialog}
        </div>
    );
};

export default ServiceStep;
